import * as z from "zod/v4";
import { serverLogger } from "~/logger.server";
import { I18N_ERROR_KEYS, type I18nErrorKey } from "./i18n-error";

export const supportedErrorSchema = z.discriminatedUnion("name", [
	z.object({
		name: z.literal("BadRequestException"),
		message: z.string(),
		status: z.number(),
		// Kept open — we branch on the `errors` entries below, and an unknown code must not fail the parse
		code: z.string(),
		errors: z.array(
			z.discriminatedUnion("code", [
				z.object({
					message: z.string(),
					code: z.literal("password_too_short"),
					minimum_length: z.number(),
				}),
				z.object({
					message: z.string(),
					code: z.literal("password_pwned"),
					occurrences: z.number(),
				}),
				z.object({
					message: z.string(),
					code: z.literal("password_too_weak"),
					warning: z.string(),
					suggestions: z.array(z.string()),
				}),
				z.object({
					message: z.string(),
					code: z.literal("password_missing_character_type"),
					character_type: z.string(),
				}),
				z.object({
					message: z.string(),
					code: z.literal("email_not_available"),
				}),
			]),
		),
		requestID: z.string(),
		rawData: z.undefined().optional(),
		error: z.undefined().optional(),
	}),
	z.object({
		name: z.literal("UnprocessableEntityException"),
		message: z.string(),
		stack: z.string(),
		status: z.number(),
		code: z.literal("invalid_request_parameters"),
		requestID: z.string(),
		errors: z.undefined().optional(),
		rawData: z.undefined().optional(),
		error: z.undefined().optional(),
	}),
	z.object({
		name: z.literal("NotFoundException"),
		message: z.string(),
		stack: z.string().optional(),
		status: z.number(),
		code: z.string().optional(),
		requestID: z.string(),
		errors: z.undefined().optional(),
		rawData: z.undefined().optional(),
		error: z.undefined().optional(),
	}),
	z.object({
		name: z.literal("GenericServerException"),
		message: z.string(),
		stack: z.string().optional(),
		status: z.number(),
		rawData: z
			.discriminatedUnion("code", [
				z.object({
					code: z.literal("invalid_credentials"),
					message: z.string(),
				}),
				z.object({
					code: z.literal("one_time_code_expired"),
					message: z.string(),
				}),
				z.object({
					code: z.literal("invalid_one_time_code"),
					message: z.string(),
				}),
				z.object({
					code: z.literal("invalid_pending_authentication_token"),
					message: z.string(),
					pending_authentication_token: z.string(),
					email_verification_id: z.string(),
				}),
				z.object({
					code: z.literal("email_verification_required"),
					message: z.string(),
					pending_authentication_token: z.string(),
					email_verification_id: z.string(),
				}),
				z.object({
					code: z.literal("mfa_enrollment"),
					message: z.string(),
					pending_authentication_token: z.string(),
					user: z.object({ id: z.string() }),
				}),
				z.object({
					code: z.literal("mfa_challenge"),
					message: z.string(),
					pending_authentication_token: z.string(),
					authentication_factors: z.array(z.object({ id: z.string(), type: z.string() })),
				}),
				z.object({
					code: z.literal("organization_selection_required"),
					message: z.string(),
					pending_authentication_token: z.string(),
					organizations: z.array(z.object({ id: z.string(), name: z.string() })),
				}),
			])
			.optional(),
		requestID: z.string(),
		code: z.undefined().optional(),
		errors: z.undefined().optional(),
		error: z.undefined().optional(),
	}),
	z.object({
		name: z.literal("OauthException"),
		message: z.string(),
		stack: z.string().optional(),
		status: z.number(),
		error: z.string(),
		errorDescription: z.string().optional(),
		requestID: z.string(),
		code: z.undefined().optional(),
		errors: z.undefined().optional(),
		rawData: z
			.object({
				code: z.undefined().optional(),
				error: z.string(),
				email: z.string().optional(),
				connection_ids: z.array(z.string()).optional(),
			})
			.optional(),
	}),
	z.object({
		name: z.literal("I18nError"),
		message: z.enum(I18N_ERROR_KEYS),
		requestID: z.undefined().optional(),
		status: z.undefined().optional(),
		code: z.undefined().optional(),
		errors: z.undefined().optional(),
		rawData: z.undefined().optional(),
		error: z.undefined().optional(),
	}),
]);
type SupportedError = z.infer<typeof supportedErrorSchema>;

export class AuthError extends Error {
	public readonly status?: SupportedError["status"];
	public readonly requestID?: SupportedError["requestID"];
	public readonly code?: SupportedError["code"];
	public readonly errors?: SupportedError["errors"];
	public readonly oauthError?: SupportedError["error"];
	public readonly i18nKey?: I18nErrorKey;
	private readonly rawData?: SupportedError["rawData"];

	constructor(exception: unknown) {
		const result = supportedErrorSchema.safeParse(exception);
		if (!result.success) {
			serverLogger.error("Failed to parse WorkOS error with supported schema", result.error, {
				logGroup: "WORKOS_ERROR_PARSING_FAILED",
				exception,
			});
			super("An unexpected error occurred");
			this.name = "AuthError";
			return;
		}
		const props = result.data;
		super(props.message);

		this.name = props.name;
		this.status = props.status;
		this.requestID = props.requestID;
		this.code = props.code;
		this.rawData = props.rawData;
		this.errors = props.errors;
		this.oauthError = props.error;
		if (props.name === "I18nError") {
			this.i18nKey = props.message;
		}
	}

	public getRawData() {
		return this.rawData;
	}

	public toObject() {
		return {
			name: this.name,
			message: this.message,
			status: this.status,
			requestID: this.requestID,
			code: this.code,
			errors: this.errors,
		};
	}
}
